import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  isProcedureExamType,
  NORMAL_WORKSHEET_BY_TYPE,
  usesMultiIndication,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { ImagingInstance } from "$global-providers/rxdb-provider/collections/imaging-instance";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import type { RxCollection } from "rxdb";
import {
  GE_PRESET_TO_EXAM_TYPE,
  isStandardCounterpart,
} from "../../../_lib/dicom-preset-mapping";
import {
  collectAssignedExamImageIds,
  imagingInstanceToExamImage,
} from "../../../_lib/hooks/use-encounter-imaging-instances";

export const autoAssignedExamType = (
  instance: ImagingInstance,
  offeredExamTypes: readonly ExamType[],
): ExamType | null => {
  const presetType = instance.dicomPreset
    ? GE_PRESET_TO_EXAM_TYPE[instance.dicomPreset]
    : null;
  if (!presetType) return null;
  if (offeredExamTypes.includes(presetType)) return presetType;
  const usCandidates = offeredExamTypes.filter((examType) =>
    isStandardCounterpart(examType, presetType),
  );
  return usCandidates.length === 1 ? usCandidates[0] : null;
};

/**
 * PNE-213: Watches imaging instances for an encounter and auto-assigns those
 * with unambiguous DICOM presets to their matching exam worksheet type.
 *
 * - TTE   → cardiac
 * - CHEST → lung
 * - VASCULAR → aorta
 *
 * Creates the exam if one doesn't already exist for this encounter; otherwise
 * patches its images array with any new instances (deduplicated by id).
 * Instances with ambiguous or unknown presets are left unassigned, as are
 * instances another exam in the encounter already holds. A guided procedure
 * can be scanned under any preset, so an image with no exam of its own type
 * goes onto the encounter's procedure exam when there is exactly one, and is
 * left unassigned when there are several.
 *
 * Note: the findOne→insert sequence is not strictly atomic. In the rare case
 * where two concurrent effect runs both observe !existing and both attempt an
 * insert, the second will throw a conflict error which is caught and ignored —
 * the first insert wins and no data is lost.
 */
export function useAutoAssignDicomImages({
  encounterId,
  instances,
  examCollection,
  userId,
  offeredExamTypes,
}: {
  encounterId: string | null | undefined;
  instances: ReadonlyArray<ImagingInstance>;
  examCollection: RxCollection<EpocusExam> | null;
  userId: string;
  offeredExamTypes: readonly ExamType[];
}): void {
  useEffect(() => {
    if (!encounterId || instances.length === 0) return;
    const collection = examCollection;
    if (!collection) return;

    // Group instances by their unambiguous exam type
    const grouped = new Map<ExamType, ImagingInstance[]>();
    for (const inst of instances) {
      const examType = autoAssignedExamType(inst, offeredExamTypes);
      if (!examType) continue;
      const list = grouped.get(examType) ?? [];
      list.push(inst);
      grouped.set(examType, list);
    }

    if (grouped.size === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const encounterExams = await collection
          .find({ selector: { encounterId } })
          .exec();
        if (cancelled) return;

        const procedureExams = encounterExams.filter((exam) =>
          isProcedureExamType(exam.examType),
        );

        for (const [examType, insts] of grouped) {
          if (cancelled) break;

          const sameType = encounterExams.find(
            (exam) => exam.examType === examType,
          );
          if (!sameType && procedureExams.length > 1) continue;
          const existing = sameType ?? procedureExams[0];
          // Only the clinician who signed a report takes late images onto it.
          if (existing?.signedAt && existing.signedByUserId !== userId)
            continue;
          const claimedElsewhere = collectAssignedExamImageIds(
            encounterExams
              .filter((exam) => exam !== existing)
              .map((exam) => exam._data as EpocusExam),
          );
          const unclaimed = insts.filter(
            (inst) => !claimedElsewhere.has(inst.id),
          );
          if (unclaimed.length === 0) continue;

          const newImages = unclaimed.map(imagingInstanceToExamImage);

          if (!existing) {
            const normalData = NORMAL_WORKSHEET_BY_TYPE[examType];
            const emptyViews = Object.fromEntries(
              Object.keys(normalData.views).map((k) => [k, false]),
            );
            const now = new Date().toISOString();
            try {
              await collection.insert({
                id: nanoid(),
                encounterId,
                examType,
                status: "draft",
                signedAt: null,
                signedByUserId: null,
                nonBillable: false,
                nonBillableReason: null,
                amendments: [],
                worksheetData: JSON.parse(
                  JSON.stringify({
                    ...normalData,
                    indication: usesMultiIndication(examType) ? [] : null,
                    views: emptyViews,
                  }),
                ) as EpocusExam["worksheetData"],
                images: newImages,
                meta: {
                  createdAt: now,
                  createdByUserId: userId,
                  updatedAt: now,
                  updatedByUserId: userId,
                },
              });
            } catch {
              // Concurrent insert won the race — the exam already exists.
              // Re-fetch and merge images so we don't silently drop them.
              if (cancelled) return;
              const created = await collection
                .findOne({ selector: { encounterId, examType } })
                .exec();
              if (created) {
                await created.incrementalModify((docData) => {
                  const currentIds = new Set(
                    docData.images.map((img) => img.id),
                  );
                  const additions = newImages.filter(
                    (img) => !currentIds.has(img.id),
                  );
                  if (additions.length === 0) return docData;
                  const updatedAt = new Date().toISOString();
                  return {
                    ...docData,
                    images: [...docData.images, ...additions],
                    meta: {
                      ...docData.meta,
                      updatedAt,
                      updatedByUserId: userId,
                    },
                  };
                });
              }
            }
          } else {
            // Use incrementalModify for an atomic read-modify-write so concurrent
            // writes don't clobber each other's images.
            await existing.incrementalModify((docData) => {
              const currentIds = new Set(docData.images.map((img) => img.id));
              const additions = newImages.filter(
                (img) => !currentIds.has(img.id),
              );
              if (additions.length === 0) return docData;
              const updatedAt = new Date().toISOString();
              return {
                ...docData,
                images: [...docData.images, ...additions],
                meta: { ...docData.meta, updatedAt, updatedByUserId: userId },
              };
            });
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to auto-assign DICOM images", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [encounterId, instances, userId, examCollection, offeredExamTypes]);
}
